'use strict';

/**
 * Rail -- the shared left rail. It hosts the site navigation and the notebook in one panel, and a single
 * top-bar toggle cycles three modes:
 *   nav      -- navigation only
 *   notebook -- notebook only
 *   split    -- notebook on top, navigation on bottom, with a draggable horizontal splitter between them
 * On a phone (<=860px, where the shell already flips the left panel to a drawer) split is dropped: the
 * toggle just swaps nav <-> notebook and the active pane fills the drawer as its own view.
 *
 * Switching modes is CSS-only (a data-rail-mode attribute on the root), so the notebook's editors are
 * never torn down on a toggle. Mode and split ratio persist in localStorage so the rail returns the way it
 * was left. The two things the rail can't own itself -- mounting the notebook view (an app view) into the
 * notebook pane, and the top-bar toggle button's icon -- it asks the app to do via this.pict.PictApplication.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');

const _MODE_KEY = 'plansheet.rail.mode';
const _RATIO_KEY = 'plansheet.rail.splitratio';
const _MOBILE_QUERY = '(max-width: 860px)';
const _MIN_NOTEBOOK = 140;   // px -- the split never shrinks a pane below these
const _MIN_NAV = 120;

class PictViewRail extends libPictView
{
	_root() { return (typeof document !== 'undefined') ? document.getElementById('Plansheet-Rail-Root') : null; }
	_app() { return this.pict.PictApplication; }

	isMobile() { return (typeof window !== 'undefined') && !!window.matchMedia && window.matchMedia(_MOBILE_QUERY).matches; }

	// The persisted value is the user's INTENT (nav / notebook / split). getMode returns it; setMode applies
	// the EFFECTIVE mode -- split collapses to notebook on a phone -- but persists the intent, so split
	// comes back when the viewport is wide again.
	getMode()
	{
		try { return localStorage.getItem(_MODE_KEY) || 'nav'; } catch (pError) { return 'nav'; }
	}

	setMode(pMode)
	{
		let tmpIntent = (pMode === 'notebook' || pMode === 'split') ? pMode : 'nav';
		try { localStorage.setItem(_MODE_KEY, tmpIntent); } catch (pError) { /* best effort */ }
		let tmpEffective = (tmpIntent === 'split' && this.isMobile()) ? 'notebook' : tmpIntent;   // no split on a phone
		let tmpRoot = this._root();
		if (tmpRoot) { tmpRoot.setAttribute('data-rail-mode', tmpEffective); }
		let tmpApp = this._app();
		// The notebook is an app view; mount it the first time it becomes visible.
		if ((tmpEffective === 'notebook' || tmpEffective === 'split') && tmpApp && typeof tmpApp._ensureNotebookMounted === 'function') { tmpApp._ensureNotebookMounted(); }
		if (tmpApp && typeof tmpApp._updateRailToggle === 'function') { tmpApp._updateRailToggle(tmpEffective); }
		return tmpEffective;
	}

	cycle()
	{
		let tmpOrder = this.isMobile() ? ['nav', 'notebook'] : ['nav', 'notebook', 'split'];
		let tmpIdx = tmpOrder.indexOf(this.getMode());
		return this.setMode(tmpOrder[(tmpIdx + 1) % tmpOrder.length]);
	}

	_applyRatio(pRatio)
	{
		let tmpRoot = this._root();
		if (tmpRoot && pRatio > 0 && pRatio < 1) { tmpRoot.style.setProperty('--ps-rail-split', (pRatio * 100) + '%'); }
	}

	// A dependency-free horizontal splitter: drag the divider to redistribute height between the notebook
	// (top) and the nav (bottom). Pointer capture keeps the drag alive past the 6px strip; rAF coalesces
	// moves; the ratio clamps so neither pane drops below its minimum, and persists on release.
	_wireSplitter()
	{
		if (typeof document === 'undefined') { return; }
		let tmpSplitter = document.getElementById('Plansheet-RailSplitter');
		let tmpRoot = this._root();
		if (!tmpSplitter || !tmpRoot || tmpSplitter._railWired) { return; }
		tmpSplitter._railWired = true;
		let tmpSelf = this;
		let tmpDragging = false, tmpRect = null, tmpRaf = 0, tmpY = 0;
		let fApply = () =>
		{
			tmpRaf = 0;
			if (!tmpRect || tmpRect.height <= 0) { return; }
			let tmpRatio = (tmpY - tmpRect.top) / tmpRect.height;
			let tmpMin = _MIN_NOTEBOOK / tmpRect.height;
			let tmpMax = (tmpRect.height - _MIN_NAV) / tmpRect.height;
			tmpRatio = Math.max(tmpMin, Math.min(tmpMax, tmpRatio));
			tmpRoot.style.setProperty('--ps-rail-split', (tmpRatio * 100) + '%');
			tmpSelf._splitRatio = tmpRatio;
		};
		tmpSplitter.addEventListener('pointerdown', (pEvent) =>
		{
			tmpDragging = true; tmpRect = tmpRoot.getBoundingClientRect();
			try { tmpSplitter.setPointerCapture(pEvent.pointerId); } catch (pErr) { /* */ }
			pEvent.preventDefault();
		});
		tmpSplitter.addEventListener('pointermove', (pEvent) =>
		{
			if (!tmpDragging) { return; }
			tmpY = pEvent.clientY;
			if (!tmpRaf) { tmpRaf = requestAnimationFrame(fApply); }
		});
		let fEnd = (pEvent) =>
		{
			if (!tmpDragging) { return; }
			tmpDragging = false;
			try { tmpSplitter.releasePointerCapture(pEvent.pointerId); } catch (pErr) { /* */ }
			try { localStorage.setItem(_RATIO_KEY, String(tmpSelf._splitRatio)); } catch (pErr) { /* */ }
		};
		tmpSplitter.addEventListener('pointerup', fEnd);
		tmpSplitter.addEventListener('pointercancel', fEnd);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		let tmpRatio = parseFloat((typeof localStorage !== 'undefined' && localStorage.getItem(_RATIO_KEY)) || '');
		this._splitRatio = (!isNaN(tmpRatio) && tmpRatio > 0 && tmpRatio < 1) ? tmpRatio : 0.5;
		this._applyRatio(this._splitRatio);
		this._wireSplitter();
		// Re-apply the mode when the viewport crosses the mobile breakpoint, so split collapses to notebook
		// on a phone and reopens on a wide screen without a reload. Bound once.
		if (typeof window !== 'undefined' && window.matchMedia && !this._mqlBound)
		{
			this._mqlBound = true;
			let tmpSelf = this;
			let tmpMql = window.matchMedia(_MOBILE_QUERY);
			let fChange = () => { tmpSelf.setMode(tmpSelf.getMode()); };
			if (tmpMql.addEventListener) { tmpMql.addEventListener('change', fChange); }
			else if (tmpMql.addListener) { tmpMql.addListener(fChange); }
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewRail;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Rail',
	DefaultRenderable: 'Plansheet-Rail',
	DefaultDestinationAddress: '#Plansheet-Sidebar',
	AutoRender: false,
	CSS: /*css*/`
		.ps-rail { display: flex; flex-direction: column; height: 100%; min-height: 0; --ps-rail-split: 50%; }
		.ps-rail-pane { min-height: 0; overflow-y: auto; }
		.ps-rail-nav { flex: 1 1 auto; }
		.ps-rail-notebook { flex: 1 1 auto; }
		.ps-rail-splitter { display: none; flex: 0 0 6px; cursor: row-resize; background: var(--theme-color-border-default, #cfc6b0); }
		.ps-rail-splitter:hover { background: var(--theme-color-brand-primary, #3b6fd4); }
		/* Mode is a CSS-only swap on the root, so the notebook's editors survive a toggle. */
		.ps-rail[data-rail-mode="nav"] .ps-rail-notebook { display: none; }
		.ps-rail[data-rail-mode="nav"] .ps-rail-splitter { display: none; }
		.ps-rail[data-rail-mode="notebook"] .ps-rail-nav { display: none; }
		.ps-rail[data-rail-mode="notebook"] .ps-rail-splitter { display: none; }
		.ps-rail[data-rail-mode="split"] .ps-rail-notebook { flex: 0 0 var(--ps-rail-split, 50%); }
		.ps-rail[data-rail-mode="split"] .ps-rail-splitter { display: block; }
		.ps-rail[data-rail-mode="split"] .ps-rail-nav { flex: 1 1 auto; }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-Rail',
			Template: /*html*/`
<div class="ps-rail" data-rail-mode="notebook" id="Plansheet-Rail-Root">
	<div class="ps-rail-pane ps-rail-notebook" id="Plansheet-Notebook"></div>
	<div class="ps-rail-splitter" id="Plansheet-RailSplitter" title="Drag to resize"></div>
	<div class="ps-rail-pane ps-rail-nav" id="Plansheet-RailNav"></div>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-Rail',
			TemplateHash: 'Plansheet-Rail',
			ContentDestinationAddress: '#Plansheet-Sidebar'
		}
	]
};
